/* eslint-disable @typescript-eslint/consistent-type-definitions */

import type { EventType } from '@/constants/geo';

export type ThemeMode = 'dark' | 'light';

export interface ElementSizes {
  circleImage: string;
  horizontalImageWidth: string;
  horizontalImageHeight: string;
  portraitImageWidth: string;
  portraitImageHeight: string;
  squareImageWidth: string;
  squareImageHeight: string;
  bannerImageWidth: string;
  bannerImageHeight: string;
  smallImageWidth: string;
  smallImageHeight: string;
  largeImageWidth: string;
  largeImageHeight: string;
  iconWidth: string;
  iconHeight: string;
  logoWidth: string;
  logoHeight: string;
  videoWidth: string;
  videoHeight: string;
  mapSize: string;
  containerWidth: string;
  containerHeight: string;
  titleFontSize: string;
  descriptionXxsmallFontSize: string;
  descriptionXsmallFontSize: string;
  descriptionLargeFontSize: string;
  descriptionMediumFontSize: string;
  descriptionSmallFontSize: string;
  menuButtonWidth: string;
  menuButtonHeight: string;
  inputWidth: string;
  inputHeight: string;
  formButtonPadding: string;
  cardContainerWidth: string;
  cardContainerHeight: string;
  [key: string]: string;
}

export interface Fonts {
  rubikBoldItalic: string;
  rubikExtraBold: string;
}

export interface Theme {
  primaryColor: string;
  hoverColor: string;
  backgroundColor: string;
  menuColor: string;
  menuGradient?: string;
  textColor: string;
  gradientColors: [string, string];
  borderColor: string;
  shadowDarkColor: string;
  shadowLightColor: string;
  titleColor: string;
  descriptionColor: string;
  descriptionBackgroundColor: string;
  iconColor: string;
  MenuItemSelectedColor: string;
  circleTextColor: string;
  themeSelectColor: string;
  titleGradientColors?: [string, string, string?];
  titleGradientAngle?: string;
  titleStrokeColor?: string;
  socialPhoneIconColor?: string;
  socialPhoneIconBackground?: string;
  socialPhoneIconBorderColor?: string;
  mobileMenuIconBackground?: string;

  sizes: {
    small: ElementSizes;
    medium: ElementSizes;
    large: ElementSizes;
    ultra: ElementSizes;
  };

  fonts: Fonts;
}

/* ---------- Small ---------- */

const small: ElementSizes = {
  circleImage: '180px',
  horizontalImageWidth: '250px',
  horizontalImageHeight: '300px',
  portraitImageWidth: '200px',
  portraitImageHeight: '300px',
  squareImageWidth: '280px',
  squareImageHeight: '280px',
  bannerImageWidth: '90vw',
  bannerImageHeight: '200px',
  smallImageWidth: '60px',
  smallImageHeight: '60px',
  largeImageWidth: '70vw',
  largeImageHeight: '70vh',
  iconWidth: '25px',
  iconHeight: '25px',
  logoWidth: '200px',
  logoHeight: '30px',
  videoWidth: '100%',
  videoHeight: '100%',
  mapSize: '200px',
  containerWidth: '90%',
  containerHeight: '100%',
  titleFontSize: '27px',
  descriptionXxsmallFontSize: '13px',
  descriptionXsmallFontSize: '16px',
  descriptionLargeFontSize: '22px',
  descriptionMediumFontSize: '13px',
  descriptionSmallFontSize: '15px',
  menuButtonWidth: '100px',
  menuButtonHeight: '40px',
  inputWidth: '70%',
  inputHeight: '8px',
  formButtonPadding: '5px 20px',
  cardContainerWidth: '300px',
  cardContainerHeight: '600px',
};

/* ---------- Medium ---------- */

const medium: ElementSizes = {
  circleImage: '400px',
  horizontalImageWidth: '300px',
  horizontalImageHeight: '300px',
  portraitImageWidth: '250px',
  portraitImageHeight: '350px',
  squareImageWidth: '320px',
  squareImageHeight: '320px',
  bannerImageWidth: '85vw',
  bannerImageHeight: '250px',
  smallImageWidth: '75px',
  smallImageHeight: '75px',
  largeImageWidth: '35vw',
  largeImageHeight: '65vh',
  iconWidth: '30px',
  iconHeight: '30px',
  logoWidth: '200px',
  logoHeight: '30px',
  videoWidth: '80vw',
  videoHeight: '80vh',
  mapSize: '200px',
  containerWidth: '85%',
  containerHeight: '100%',
  titleFontSize: '40px',
  descriptionXxsmallFontSize: '16px',
  descriptionXsmallFontSize: '18px',
  descriptionLargeFontSize: '25px',
  descriptionMediumFontSize: '22px',
  descriptionSmallFontSize: '20px',
  menuButtonWidth: '80px',
  menuButtonHeight: '40px',
  inputWidth: '70%',
  inputHeight: '10px',
  formButtonPadding: '10px 30px',
  cardContainerWidth: '500px',
  cardContainerHeight: '500px',
};

/* ---------- Large ---------- */

const large: ElementSizes = {
  circleImage: '600px',
  horizontalImageWidth: '400px',
  horizontalImageHeight: '500px',
  portraitImageWidth: '250px',
  portraitImageHeight: '550px',
  squareImageWidth: '360px',
  squareImageHeight: '360px',
  bannerImageWidth: '70vw',
  bannerImageHeight: '280px',
  smallImageWidth: '120px',
  smallImageHeight: '120px',
  largeImageWidth: '25vw',
  largeImageHeight: '60vh',
  iconWidth: '30px',
  iconHeight: '30px',
  logoWidth: '200px',
  logoHeight: '30px',
  videoWidth: '60vw',
  videoHeight: '60vh',
  mapSize: '200px',
  containerWidth: '65%',
  containerHeight: '100%',
  titleFontSize: '50px',
  descriptionXxsmallFontSize: '18px',
  descriptionXsmallFontSize: '20px',
  descriptionLargeFontSize: '23px',
  descriptionMediumFontSize: '22px',
  descriptionSmallFontSize: '21px',
  menuButtonWidth: '110px',
  menuButtonHeight: '40px',
  inputWidth: '70%',
  inputHeight: '12px',
  formButtonPadding: '10px 40px',
  cardContainerWidth: '600px',
  cardContainerHeight: '600px',
};

/* ---------- Ultra ---------- */

const ultra: ElementSizes = {
  ...large,
  titleFontSize: '72px',
  containerWidth: '55%',
  videoWidth: '50vw',
  videoHeight: '55vh',
  descriptionXxsmallFontSize: '20px',
  descriptionXsmallFontSize: '22px',
  descriptionMediumFontSize: '24px',
  descriptionSmallFontSize: '26px',
  descriptionLargeFontSize: '30px',
  smallImageWidth: '140px',
  smallImageHeight: '140px',

  
};

const sizes: Theme['sizes'] = { small, medium, large, ultra };

/* ---------- Fonts ---------- */

const fonts: Fonts = {
  rubikBoldItalic: 'Rubik-BoldItalic',
  rubikExtraBold: 'Rubik-ExtraBold',
};

/* ---------- Themes ---------- */

export const darkTheme: Theme = {
  primaryColor: '#0f0f0f',
  hoverColor: '#ffffff',
  backgroundColor: '#000000',
  menuColor: '#000000',
  textColor: '#ffffff',
  gradientColors: ['rgb(65,118,204)', 'rgb(182,72,182)'],
  borderColor: '#b341c4',
  shadowDarkColor: '#000000',
  shadowLightColor: '#ffffff',
  titleColor: 'rgb(130,48,160)',
  descriptionColor: '#ffffff',
  descriptionBackgroundColor: 'rgba(52,36,36,0.56)',
  iconColor: '#000000',
  MenuItemSelectedColor: '#8943a9',
  circleTextColor: 'rgb(135,49,169)',
  themeSelectColor: '#ffffff',
  socialPhoneIconColor: '#24102f',
  socialPhoneIconBackground: 'linear-gradient(135deg, #ffffff 0%, #efe3ff 52%, #c38aff 100%)',
  socialPhoneIconBorderColor: 'rgba(137, 67, 169, 0.34)',
  mobileMenuIconBackground: '#a24fc8',
  sizes,
  fonts,
};

export const lightTheme: Theme = {
  primaryColor: '#6a1b9a',
  hoverColor: '#2979FF',
  backgroundColor: '#ffffff',
  menuColor: '#ffffff',
  textColor: '#333333',
  gradientColors: ['rgb(65,118,204)', 'rgb(182,72,182)'],
  borderColor: '#a24fc8',
  shadowDarkColor: '#f8f8f8',
  shadowLightColor: '#ffffff',
  titleColor: 'rgb(161,39,198)',
  descriptionColor: '#000000',
  descriptionBackgroundColor: 'rgba(255,255,255,0.3)',
  iconColor: '#000000',
  MenuItemSelectedColor: '#a24fc8',
  circleTextColor: '#b341c4',
  themeSelectColor: '#000000',
  socialPhoneIconColor: '#24102f',
  socialPhoneIconBackground: 'linear-gradient(135deg, #ffffff 0%, #efe3ff 52%, #c38aff 100%)',
  socialPhoneIconBorderColor: 'rgba(137, 67, 169, 0.34)',
  sizes,
  fonts,
};

export const hennaDarkTheme: Theme = {
  ...darkTheme,
  primaryColor: '#071a33',
  hoverColor: '#f1cf7a',
  backgroundColor: '#020713',
  menuColor: '#06142a',
  textColor: '#fff4d8',
  gradientColors: ['#174a86', '#d8a943'],
  borderColor: '#d8a943',
  shadowDarkColor: '#000000',
  shadowLightColor: 'rgba(0, 0, 0, 0.79)',
  titleColor: '#dbb85e',
  descriptionColor: '#fff0bf',
  iconColor: '#06142a',
  MenuItemSelectedColor: '#f1cf7a',
  circleTextColor: '#f1cf7a',
  themeSelectColor: '#f1cf7a',
  titleGradientColors: ['#174a86', '#f1cf7a', '#f1cf7a'],
  titleGradientAngle: '135deg',
  titleStrokeColor: 'rgba(2, 7, 19, 0.72)',
  socialPhoneIconColor: '#06142a',
  socialPhoneIconBackground: 'linear-gradient(135deg, #fff6d9 0%, #f1cf7a 52%, #174a86 100%)',
  socialPhoneIconBorderColor: 'rgba(241, 207, 122, 0.48)',
  mobileMenuIconBackground: '#f1cf7a',
};

export const hennaLightTheme: Theme = {
  ...lightTheme,
  primaryColor: '#a31616',
  hoverColor: '#b8871f',
  backgroundColor: '#fffaf0',
  menuColor: '#fff6df',
  textColor: '#2a0808',
  gradientColors: ['#b11d1d', '#d6a842'],
  borderColor: '#7d1212',
  shadowDarkColor: 'rgba(255, 250, 240, 0.88)',
  shadowLightColor: '#fff8de',
  titleColor: '#a31616',
  descriptionColor: '#220606',
  descriptionBackgroundColor: 'rgba(255, 246, 223, 0.58)',
  iconColor: '#2a0808',
  MenuItemSelectedColor: '#b8871f',
  circleTextColor: '#a31616',
  themeSelectColor: '#7d1212',
  titleGradientColors: ['#a31616', '#d6a842', '#7d1212'],
  titleGradientAngle: '135deg',
  titleStrokeColor: 'rgba(255, 250, 240, 0.85)',
  socialPhoneIconColor: '#2a0808',
  socialPhoneIconBackground: 'linear-gradient(135deg, #fffaf0 0%, #d6a842 52%, #a31616 100%)',
  socialPhoneIconBorderColor: 'rgba(163, 22, 22, 0.38)',
};

export const weddingDarkTheme: Theme = {
  ...darkTheme,
  primaryColor: '#070604',
  hoverColor: '#f4d57b',
  backgroundColor: '#050403',
  menuColor: '#090705',
  textColor: '#fff7df',
  gradientColors: ['#0a0704', '#d2a33b'],
  borderColor: '#d2a33b',
  shadowDarkColor: '#000000',
  shadowLightColor: 'rgba(0, 0, 0, 0.78)',
  titleColor: '#e7c15a',
  descriptionColor: '#fff1c8',
  descriptionBackgroundColor: 'rgba(8, 6, 3, 0.68)',
  iconColor: '#090705',
  MenuItemSelectedColor: '#e7c15a',
  circleTextColor: '#e7c15a',
  themeSelectColor: '#f4d57b',
  titleGradientColors: ['#f8e19a', '#d2a33b', '#8c6517'],
  titleGradientAngle: '135deg',
  titleStrokeColor: 'rgba(0, 0, 0, 0.78)',
  socialPhoneIconColor: '#090705',
  socialPhoneIconBackground: 'linear-gradient(135deg, #fff4c5 0%, #e7c15a 52%, #6b4710 100%)',
  socialPhoneIconBorderColor: 'rgba(231, 193, 90, 0.48)',
  mobileMenuIconBackground: '#f4d57b',
};

export const weddingLightTheme: Theme = {
  ...lightTheme,
  primaryColor: '#9f3e32',
  hoverColor: '#b9832f',
  backgroundColor: '#fff3ee',
  menuColor: '#fff0e8',
  textColor: '#2b0d08',
  gradientColors: ['#b85a48', '#d1a048'],
  borderColor: '#b85a48',
  shadowDarkColor: 'rgba(255, 243, 238, 0.9)',
  shadowLightColor: '#fff7ed',
  titleColor: '#9f3e32',
  descriptionColor: '#2b0d08',
  descriptionBackgroundColor: 'rgba(255, 240, 232, 0.62)',
  iconColor: '#2b0d08',
  MenuItemSelectedColor: '#b9832f',
  circleTextColor: '#9f3e32',
  themeSelectColor: '#9f3e32',
  titleGradientColors: ['#9f3e32', '#d1a048', '#7f2d28'],
  titleGradientAngle: '135deg',
  titleStrokeColor: 'rgba(255, 243, 238, 0.88)',
  socialPhoneIconColor: '#2b0d08',
  socialPhoneIconBackground: 'linear-gradient(135deg, #fff3ee 0%, #d1a048 52%, #b85a48 100%)',
  socialPhoneIconBorderColor: 'rgba(159, 62, 50, 0.38)',
};

export const eventThemes: Record<EventType, Record<ThemeMode, Theme>> = {
  'אירוע': {
    dark: weddingDarkTheme,
    light: weddingLightTheme,
  },
  'חתונה': {
    dark: weddingDarkTheme,
    light: weddingLightTheme,
  },
  'חינה': {
    dark: hennaDarkTheme,
    light: hennaLightTheme,
  },
  'בר-מצווה': {
    dark: weddingDarkTheme,
    light: weddingLightTheme,
  },
};

export const getThemeForEvent = (
  eventType: EventType = 'אירוע',
  mode: ThemeMode = 'dark'
): Theme => eventThemes[eventType]?.[mode] ?? eventThemes['אירוע'][mode];
